import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Heart, Sparkles, Target } from "lucide-react";
import { motion } from "framer-motion";

const TTCFertileCard = ({ prediction }) => {
  if (!prediction?.fertile_window_start) return null;

  const today = new Date();
  const start = new Date(prediction.fertile_window_start);
  const end = new Date(prediction.fertile_window_end);
  const ovulation = new Date(prediction.next_ovulation);

  const dayMs = 1000 * 60 * 60 * 24;
  const daysToStart = Math.floor((start - today) / dayMs);
  const daysToOvulation = Math.floor((ovulation - today) / dayMs);

  const inWindow = today >= start && today <= end;
  const isOvulationDay = Math.abs(daysToOvulation) === 0;

  const windowStatus = inWindow
    ? (isOvulationDay ? "Peak fertility today" : "You're in your fertile window")
    : daysToStart > 0
      ? `Fertile window opens in ${daysToStart} days`
      : "Fertile window closed — ovulation passed";

  const advice = inWindow
    ? (isOvulationDay
        ? "Today is your peak fertile day. Connection, nourishment, and rest are most supportive."
        : "Your body is at its most receptive. Stay hydrated, eat warm nourishing meals, and prioritize connection.")
    : daysToStart > 0 && daysToStart <= 5
      ? "Your fertile window is approaching. Gentle movement, whole foods, and stress reduction all help."
      : "Use this time for rest and preparation — iron-rich foods, magnesium, and quality sleep build cycle health.";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      data-testid="ttc-fertile-card"
    >
      <Card className={`glass bg-gradient-to-br ${inWindow ? 'from-rose-500/20 via-pink-500/10 to-transparent border-rose-400/40 shadow-[0_0_30px_rgba(244,63,94,0.15)]' : 'from-rose-500/10 via-pink-500/5 to-transparent border-rose-400/20'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Heart className={`w-5 h-5 ${inWindow ? 'text-rose-300 animate-pulse' : 'text-rose-400'}`} />
              Fertility Focus
            </CardTitle>
            {inWindow && (
              <span className="text-xs px-3 py-1 rounded-full bg-rose-500/20 text-rose-200 border border-rose-400/40 flex items-center gap-1" data-testid="fertile-active-badge">
                <Sparkles className="w-3 h-3" /> Active
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-baseline gap-2">
            <Target className="w-4 h-4 text-rose-300" />
            <p className="text-white text-lg font-semibold" data-testid="fertile-window-status">
              {windowStatus}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-mystic-900/50 p-3 border border-white/5 text-center">
              <p className="text-rose-200/80 text-[10px] uppercase tracking-wider mb-1">Fertile From</p>
              <p className="text-white text-sm font-medium" data-testid="fertile-start-date">
                {prediction.fertile_window_start.slice(5)}
              </p>
            </div>
            <div className={`rounded-xl p-3 text-center border ${isOvulationDay ? 'bg-rose-500/25 border-rose-400/50' : 'bg-mystic-900/50 border-white/5'}`}>
              <p className="text-rose-200/80 text-[10px] uppercase tracking-wider mb-1">Ovulation</p>
              <p className="text-white text-sm font-medium" data-testid="ovulation-date">
                {prediction.next_ovulation.slice(5)}
              </p>
            </div>
            <div className="rounded-xl bg-mystic-900/50 p-3 border border-white/5 text-center">
              <p className="text-rose-200/80 text-[10px] uppercase tracking-wider mb-1">Fertile Until</p>
              <p className="text-white text-sm font-medium" data-testid="fertile-end-date">
                {prediction.fertile_window_end.slice(5)}
              </p>
            </div>
          </div>

          <p className="text-mystic-100 text-sm leading-relaxed" data-testid="fertile-advice">
            {advice}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TTCFertileCard;
